import {
  useCreateDocumentMutation,
  usePatchDocumentMutation,
} from "@hooks";
import { Document } from "@models";
import { useQueryClient } from "@tanstack/react-query";
import { RegulatoryTypeEnum } from "@types";
import { useNavigate } from "react-router-dom";
import {
  baseDocumentsQueryKey,
  getDocumentsQueryKey,
} from "src/config/request-keys";
import { buildDocumentRoute } from "src/utils/navigation";

type UseCustomTemplateOptions = {
  folderId?: string | null;
  navigationDeviceId?: string | null;
};

export const useCustomTemplate = (
  orgId: string,
  deviceId: string | null,
  options: UseCustomTemplateOptions = {}
) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const targetFolderId = options.folderId ?? null;
  const navigationDeviceId = deviceId || options.navigationDeviceId;
  const documentsQueryKey = deviceId
    ? getDocumentsQueryKey(deviceId)
    : baseDocumentsQueryKey;
  const navigateToDocument = (document: Document, versionId?: string) => {
    navigate(
      navigationDeviceId
        ? buildDocumentRoute({
            orgId,
            deviceId: navigationDeviceId,
            documentId: document.id,
            versionId,
          })
        : `/${orgId}/documents`
    );
  };

  const createDocumentMutation = useCreateDocumentMutation();
  const patchDocumentMutation = usePatchDocumentMutation();

  const getInitialDocumentVersion = (document: Document) => {
    const documentVersion = document.versions?.[0];

    if (!documentVersion) {
      throw new Error("Created document did not include an initial version.");
    }

    return documentVersion;
  };

  const assignDocumentToTargetFolder = async (document: Document) => {
    if (!targetFolderId || document.folderId === targetFolderId) {
      return document;
    }

    return patchDocumentMutation.mutateAsync({
      orgId,
      docId: document.id,
      deviceId: deviceId ?? "",
      queryDeviceIds: deviceId ? [deviceId] : [],
      data: {
        folderId: targetFolderId,
      },
    });
  };

  const createTemplateDocument = async ({
    documentName,
    regulatoryType,
    templateKey,
  }: {
    documentName: string;
    regulatoryType?: RegulatoryTypeEnum;
    templateKey: string;
  }): Promise<{ document: Document }> => {
    const document = await createDocumentMutation.mutateAsync({
      orgId,
      body: {
        ...(deviceId ? { deviceId } : {}),
        templateKey,
        title: documentName,
        ...(regulatoryType ? { regulatoryType } : {}),
      },
    });

    const documentVersion = getInitialDocumentVersion(document);
    const assignedDocument = await assignDocumentToTargetFolder(document);

    await queryClient.invalidateQueries({
      queryKey: documentsQueryKey,
    });

    navigateToDocument(document, documentVersion.id);

    return {
      document: {
        ...assignedDocument,
        versions: [documentVersion],
      },
    };
  };

  const createNewInstance = async (templateKey: string, documentName: string) =>
    createTemplateDocument({
      documentName,
      regulatoryType: RegulatoryTypeEnum.RCD,
      templateKey,
    });

  const createDocumentFromTemplate = async (
    templateKey: string,
    documentName: string
  ) => createTemplateDocument({ documentName, templateKey });

  const createCustomDocument = async (
    documentName: string
  ): Promise<{ document: Document }> => {
    const document = await createDocumentMutation.mutateAsync({
      orgId,
      body: {
        ...(deviceId ? { deviceId } : {}),
        title: documentName,
      },
    });

    const documentVersion = getInitialDocumentVersion(document);

    const assignedDocument = await assignDocumentToTargetFolder(document);

    // Invalidate the documents list cache so the new document appears in DocExplorer
    await queryClient.invalidateQueries({
      queryKey: documentsQueryKey,
    });

    navigateToDocument(document);

    return {
      document: {
        ...assignedDocument,
        versions: [documentVersion],
      },
    };
  };

  return {
    createCustomDocument,
    createDocumentFromTemplate,
    createNewInstance,
  };
};
